import { ChangeEventHandler, useEffect, useState } from "react";
import { getSpeedRateValue, recordToSelect, getDefaultValues, getSavedVoice } from "./utils";
import { useTextToSpeech, useTextToSpeechState, useTextToSpeechDispatch } from "../../../contexts/TextToSpeech";
import { TextToSpeechActionsType } from "../../../contexts/TextToSpeech/types";
import blobUrlFormatHelper from "../../blobUrlFormatHelper";
import SpeedSlider from "../../Slider/SpeedSlider";

import { ReactComponent as CopyTextIcon } from "../../../assets/icons/copy-text.svg";
import { ReactComponent as PauseIcon } from "../../../assets/icons/NarratorTab/narrator-text-pause-icon.svg";
import { ReactComponent as PlayIcon } from "../../../assets/icons/NarratorTab/Square-Button-Play.svg";
import { ReactComponent as HelpIcon } from "../../../assets/icons/TextToSpeech/Square-Button-Help.svg";
import { ReactComponent as PronunciationIcon } from "../../../assets/icons/TextToSpeech/Square-Button-Pronunciation.svg";
import { ReactComponent as GeneratingAudioSpinner } from "../../../assets/icons/HeaderIcons/loading-sequence-airplane-icon.svg";
import { ReactComponent as TranslateIcon } from "../../../assets/icons/NarratorTab/translate-icon.svg";

import "./TextToSpeechForm.css";
import InputPicker from "rsuite/InputPicker";
import { AudioPlayer } from "../../AudioPlayer";
import { generateFileName } from "../../../utils/Audio/generateFileName";
import { Tooltip } from "../../ToolTip/ToolTip";
import { TextToSpeechFormProps } from "./types";
import MachineTranslation from "../CognitiveServices/MachineTranslation/MachineTranslation";
import { useConfirmation } from "../../../contexts/Confirmation";

export const TextToSpeechForm = ({
  values: externalValues,
  onSubmit,
  onCancel,
  isAlternate = false,
}: TextToSpeechFormProps) => {
  const values = getDefaultValues(externalValues);
  // Fields
  const [narrationText, setNarrationText] = useState<string>(values.narrationText);
  const [pronunciationText, setPronunciationText] = useState<string>(values.pronunciationText);
  const [language, setLanguage] = useState<string | undefined>(values.language);
  const [voice, setVoice] = useState<string | undefined>(values.voice);
  const [prosodyRate, setProsodyRate] = useState<number>(values.prosodyRate);
  // State Flag
  const [shouldGenerateAudio, setShouldGenerateAudio] = useState<boolean>(false);
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState<boolean>(false);
  const [hasConfigurationError, setHasConfigurationError] = useState<boolean>(false);
  // Services
  const { getLanguages, getVoices, generateAudio, reset: resetTextToSpeech } = useTextToSpeech();
  const { audio: generatedAudio, languages, voices, isGenerating, isLoading: isTTSLoading } = useTextToSpeechState();
  const dispatch = useTextToSpeechDispatch();
  const { confirm } = useConfirmation();

  // Error handling
  const clearGeneratedAudio = () => {
    dispatch({
      type: TextToSpeechActionsType.SET_AUDIO,
      payload: {
        audio: null,
      },
    });
  };

  const handleAudioGenerationError = async (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : "An error occurred while generating audio.";
    setHasConfigurationError(true);
    clearGeneratedAudio();
    await confirm({
      title: "Error",
      message: errorMessage,
      type: "success",
    });
  };

  const clearConfigurationError = () => {
    setHasConfigurationError(false);
  };

  const clearConfigurationErrorAndAudio = () => {
    setHasConfigurationError(false);
    clearGeneratedAudio();
  };

  // Handlers
  const handleReset = () => {
    setLanguage(undefined);
    setPronunciationText("");

    resetTextToSpeech();
  };
  const handleCancel = () => {
    handleReset();
    onCancel?.();
  };

  const handleSubmit = () => {
    if (generatedAudio && !hasConfigurationError) {
      onSubmit({
        pronunciationText,
        language,
        voice,
        prosodyRate,
        generatedAudio,
        narrationText,
      });

      handleReset();
    } else if (canGenerateAudio) {
      setShouldGenerateAudio(false);

      void generateAudio({
        language,
        voice,
        prosodyRate: getSpeedRateValue(prosodyRate),
        narrationText: pronunciationText,
        displayName: generateFileName(),
      })
        .then((generatedAudio) => {
          if (generatedAudio.errorMessage !== null) {
            throw new Error(generatedAudio.errorMessage);
          }

          clearConfigurationError();
          onSubmit({
            pronunciationText,
            language,
            voice,
            prosodyRate,
            generatedAudio,
            narrationText,
          });

          handleReset();
        })
        .catch(handleAudioGenerationError);
    }
  };
  const handleClickPlay = (next?: () => void) => {
    if (shouldGenerateAudio && canGenerateAudio && !isGenerating) {
      // We need to generate the audio first
      void generateAudio({
        language,
        voice,
        prosodyRate: getSpeedRateValue(prosodyRate),
        narrationText: pronunciationText,
        displayName: "tts-audio",
      })
        .then((response) => {
          if (response.errorMessage !== null) {
            throw new Error(response.errorMessage);
          }

          clearConfigurationError();
          setShouldGenerateAudio(false);
          next?.();
        })
        .catch(handleAudioGenerationError);
    } else {
      next?.();
    }
  };
  const handlePronunciationTextChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setPronunciationText(event.target.value ?? "");
    setShouldGenerateAudio(true);
    clearConfigurationErrorAndAudio();
  };
  const handleNarrationTextChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setNarrationText(event.target.value ?? "");
    clearConfigurationErrorAndAudio();
  };
  const handleProsodyRateChange = (value: number) => {
    setProsodyRate(value);
    setShouldGenerateAudio(true);
    clearConfigurationErrorAndAudio();
  };
  // Flags
  const canGenerateAudio = language && voice && !isNaN(prosodyRate) && pronunciationText?.length > 0;
  const canPlay = generatedAudio?.blobPath;
  const DEFAULT_VOICE = "en-US-ChristopherNeural";
  // Effects
  useEffect(() => {
    void getLanguages({}).then(() => {
      setShouldGenerateAudio(true);
    });
  }, []);
  useEffect(() => {
    if (language) {
      void getVoices({ language }).then((voicesRecord) => {
        const voices = Object.keys(voicesRecord);
        if (voices.length > 0) {
          const savedVoice = getSavedVoice(language);
          if (savedVoice && voices.includes(savedVoice)) {
            setVoice(savedVoice);
          } else {
            if (!voice || (voice && !voices.includes(voice))) {
              if (language === "en-US") {
                //Default voice in en-US should be Christopher
                setVoice(voices.find((v) => v.includes(DEFAULT_VOICE)));
              } else {
                setVoice(voices[0]);
              }
            }
          }
          setShouldGenerateAudio(true);
        }
      });
    }
  }, [voice, language]);

  useEffect(() => {
    if (voice && language) {
      localStorage.setItem(`savedVoice-${language}`, voice);
    }
  }, [voice, language]);
  // Data
  const languageOptions = languages ? recordToSelect(languages, "Select Language") : [];
  const voiceOptions = voices ? recordToSelect(voices, "Select Voice") : [];
  // NOTE: Commented out for now, since we might enable highlight again.
  // const getSourceTextWithSelections = (narrationText: string) => {
  //   let highlightedSourceText = narrationText;
  //   // Keep current selection
  //   if (pronunciationText) {
  //     highlightedSourceText = wrapSelectionWithColor(highlightedSourceText ?? "", pronunciationText, "new");
  //   }
  //   // Show previous selections
  //   if (values?.selections?.length > 0) {
  //     for (const selection of values.selections) {
  //       highlightedSourceText = wrapSelectionWithColor(highlightedSourceText ?? "", selection, "old");
  //     }
  //   }
  //
  //   return highlightedSourceText;
  // };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <div className="tts-form-wrapper" id="dropdown-container">
        <div className="form-section">
          <label className="form-label">Narration Text:</label>
          <div className="form-field">
            <textarea
              // must be span for this to work...
              // className={classes("tts-source-text", "cpat-textarea")}
              // dangerouslySetInnerHTML={{
              //   __html: getSourceTextWithSelections(narrationText),
              // }}
              className="cpat-textarea"
              name="narrationText"
              id="narrationText"
              rows={4}
              value={narrationText}
              onChange={handleNarrationTextChange}
            />
            <div className="tts-actions">
              <Tooltip title="Copy Text">
                <button
                  className="cpat-button cpat-button--icon tts-action tts-copy-button"
                  onClick={() => {
                    setPronunciationText(narrationText);
                    setShouldGenerateAudio(true);
                    clearConfigurationErrorAndAudio();
                  }}
                >
                  <CopyTextIcon />
                </button>
              </Tooltip>
              {isAlternate && (
                <Tooltip title="Translate">
                  <button
                    className="cpat-button cpat-button--icon tts-action tts-translate-button"
                    onClick={() => setIsTranslationModalOpen(true)}
                  >
                    <TranslateIcon />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">Pronunciation:</label>
          <div className="form-field">
            <textarea
              className="cpat-textarea"
              name="pronunciationText"
              id="pronunciationText"
              rows={4}
              value={pronunciationText}
              onChange={handlePronunciationTextChange}
            />

            <div className="tts-actions">
              {/*Language*/}
              <InputPicker
                data={languageOptions}
                appearance="default"
                placeholder="Select a Language"
                value={language}
                labelKey="label"
                valueKey="value"
                onChange={(language: string) => {
                  setLanguage(language);
                  clearConfigurationErrorAndAudio();
                }}
                size="sm"
                cleanable={false}
                className="language-dropdown"
                menuMaxHeight={150}
                disabled={isTTSLoading}
                container={() => document.getElementById("dropdown-container") || document.body}
              />
              {/*Voices*/}
              <InputPicker
                data={voiceOptions}
                appearance="default"
                placeholder="Select a Voice"
                value={voice}
                labelKey="label"
                valueKey="value"
                onChange={(voice: string) => {
                  setVoice(voice);
                  setShouldGenerateAudio(true);
                  clearConfigurationErrorAndAudio();
                }}
                size="sm"
                cleanable={false}
                className="voice-dropdown"
                menuMaxHeight={150}
                disabled={isTTSLoading}
                container={() => document.getElementById("dropdown-container") || document.body}
              />
              {/*Speed*/}
              <SpeedSlider
                min={0}
                max={6}
                step={1}
                default={prosodyRate}
                class={"speed-slider"}
                labelClassName={"slider-label"}
                sliderClassName={"slider"}
                barClassName={"bar-style"}
                disabled={isTTSLoading}
                marks={[
                  { tick: 1, mark: "S" },
                  { tick: 3, mark: "M" },
                  { tick: 5, mark: "F" },
                ]}
                label={"Speed"}
                onChangeCommitted={handleProsodyRateChange}
                sliderHeaderClassName={"slider-header"}
              />
              {/*Controls*/}
              <AudioPlayer
                src={generatedAudio?.blobPath ? blobUrlFormatHelper(generatedAudio.blobPath) : null}
                autoPlay
              >
                {({ playing, ready, loading, onClickHandler }) =>
                  isGenerating || loading ? (
                    <button className="cpat-button cpat-button--icon">
                      <GeneratingAudioSpinner className="generating-spinner" />
                    </button>
                  ) : !canPlay ? (
                    <button className="tts-play-button" onClick={() => handleClickPlay()} disabled={!canGenerateAudio}>
                      <PlayIcon />
                    </button>
                  ) : (
                    ready && (
                      <button className="tts-play-button" onClick={() => handleClickPlay(onClickHandler)}>
                        {playing ? <PauseIcon /> : <PlayIcon />}
                      </button>
                    )
                  )
                }
              </AudioPlayer>
              <Tooltip title="TTS Tips and Tricks">
                <a href="/api/Asset/cpat/generic/pdf/tips.pdf" target="_blank" className="cpat-icon-button">
                  <HelpIcon />
                </a>
              </Tooltip>
              <Tooltip title="Pronunciations">
                <a href="/api/Asset/cpat/generic/pdf/pronunciations.pdf" target="_blank" className="cpat-icon-button">
                  <PronunciationIcon />
                </a>
              </Tooltip>
            </div>
          </div>
        </div>

        {/*Actions*/}

        <div className="form-actions">
          <button className="cpat-button cpat-button--label cpat-button--reverse" onClick={handleCancel}>
            Cancel
          </button>
          <button
            className="cpat-button cpat-button--label"
            onClick={handleSubmit}
            disabled={hasConfigurationError || !canGenerateAudio}
          >
            Save
          </button>
        </div>
      </div>
      {isAlternate && (
        <MachineTranslation
          initialText={narrationText}
          open={isTranslationModalOpen}
          onClose={(translatedText: string | null, details) => {
            setIsTranslationModalOpen(false);

            if (!translatedText) {
              return;
            }

            setNarrationText(translatedText);
            clearConfigurationErrorAndAudio();
          }}
        />
      )}
    </form>
  );
};
